import { useEffect, useRef, useState, type RefObject } from 'react';

const COLLAPSE_THRESHOLD = 96;
// Downward travel required in one uninterrupted direction before hiding.
// Keeps the first post-autoscroll jitter and momentum noise from collapsing
// the chrome just because the calendar opened below the threshold.
const COLLAPSE_INTENT = 48;
const TOGGLE_COOLDOWN = 300;

/**
 * useDashboardScrollCollapse — standard mobile AppBar hide-on-scroll, driven
 * by capture-phase scroll events so it works on any watched scroller: the
 * page <main> element and the calendar's internal
 * `.calendar-scroll-surface` (which owns the vertical scroll in both the
 * normal and fullscreen views; <main> itself does not scroll there).
 *
 * Deviations from MUI's useScrollTrigger, each a stated product requirement:
 *   - `data-calendar-autoscroll`: programmatic positioning to the current-time
 *     red line is not user intent and must never change chrome state.
 *   - `data-chrome-pinned`: when the chrome is pinned open, scrolling must
 *     never collapse it.
 *   - Reveal is hysteretic (any upward scroll reveals), but collapse requires
 *     a sustained downward run (`COLLAPSE_INTENT`) — the calendar can open
 *     already scrolled past the threshold, so absolute position alone must
 *     not hide the chrome.
 */
export function useDashboardScrollCollapse(
  enabled: boolean,
  scrollRef: RefObject<HTMLElement | null>,
) {
  const [trigger, setTrigger] = useState(false);
  const run = useRef(0);
  const collapsed = useRef(false);
  const lastToggleAt = useRef(0);
  // Per-surface last scrollTop; a single shared baseline would misread
  // direction when the user switches between <main> and the calendar surface.
  const lastTops = useRef(new Map<HTMLElement, number>());

  useEffect(() => {
    if (!enabled) {
      setTrigger(false);
      run.current = 0;
      collapsed.current = false;
      lastTops.current.clear();
      return;
    }

    const isWatched = (el: EventTarget | null): el is HTMLElement =>
      el instanceof HTMLElement &&
      (el === scrollRef?.current || el.classList.contains('calendar-scroll-surface'));

    const handleScroll = (event: Event) => {
      const el = event.target;
      if (!isWatched(el)) return;
      const current = el.scrollTop;

      if (document.documentElement.hasAttribute('data-calendar-autoscroll')) {
        lastTops.current.set(el, current);
        run.current = 0;
        collapsed.current = false;
        setTrigger(false);
        return;
      }

      if (document.documentElement.hasAttribute('data-chrome-pinned')) {
        run.current = 0;
        if (collapsed.current) {
          collapsed.current = false;
          setTrigger(false);
        }
        return;
      }

      const previous = lastTops.current.get(el);
      lastTops.current.set(el, current);
      if (previous === undefined) {
        run.current = 0;
        return;
      }

      const delta = current - previous;
      if (delta === 0) return;
      if (delta < 0) {
        if (collapsed.current && Math.abs(delta) < 16) return;
        if (collapsed.current && performance.now() - lastToggleAt.current < TOGGLE_COOLDOWN) {
          run.current = delta;
          return;
        }
        run.current = delta;
        if (collapsed.current) {
          collapsed.current = false;
          lastToggleAt.current = performance.now();
          setTrigger(false);
        }
        return;
      }
      if (Math.sign(delta) !== Math.sign(run.current)) run.current = 0;
      run.current += delta;
      if (current <= COLLAPSE_THRESHOLD) {
        if (collapsed.current) {
          collapsed.current = false;
          lastToggleAt.current = performance.now();
          setTrigger(false);
        }
        return;
      }
      if (run.current < COLLAPSE_INTENT) return;
      if (performance.now() - lastToggleAt.current < TOGGLE_COOLDOWN) return;
      if (!collapsed.current) {
        collapsed.current = true;
        lastToggleAt.current = performance.now();
        setTrigger(true);
      }
    };

    // scroll events do not bubble — capture phase is required to observe
    // scrolls on descendant containers like .calendar-scroll-surface.
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [enabled, scrollRef]);

  useEffect(() => {
    const root = document.documentElement;
    if (!enabled) {
      root.removeAttribute('data-calendar-scroll');
      return;
    }
    if (root.hasAttribute('data-calendar-autoscroll') || root.hasAttribute('data-chrome-pinned')) {
      return;
    }
    if (trigger) {
      root.setAttribute('data-calendar-scroll', '1');
    } else {
      root.removeAttribute('data-calendar-scroll');
    }
  }, [enabled, trigger]);

  return trigger &&
    !document.documentElement.hasAttribute('data-calendar-autoscroll') &&
    !document.documentElement.hasAttribute('data-chrome-pinned');
}
